import { useState, useEffect, useRef } from 'react';
import {
  getAllContent,
  searchContent,
  deleteContent,
  type SavedContent,
} from '@bid/client-storage';
import type { GeneratedNpc, GeneratedMission, GeneratedLocation } from '@bid/shared-types';

// Icons
const SearchIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
  </svg>
);

const LibraryIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const EmptyIcon = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ margin: '0 auto 16px', display: 'block' }}
  >
    <path d="M20 13V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7m16 0v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-5m16 0h-2.586a1 1 0 0 0-.707.293l-2.414 2.414a1 1 0 0 1-.707.293h-3.172a1 1 0 0 1-.707-.293l-2.414-2.414A1 1 0 0 0 6.586 13H4" />
  </svg>
);

const EyeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// NPC Detail View
const NPCDetailView = ({ npc }: { npc: GeneratedNpc }) => {
  const sectionStyle: React.CSSProperties = {
    marginBottom: '24px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#d4af37',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    marginBottom: '12px',
  };

  const infoGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  };

  const infoItemStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  };

  const infoLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  };

  const infoValueStyle: React.CSSProperties = {
    fontSize: '16px',
    color: '#e5e7eb',
    fontWeight: 500,
  };

  const ratingsGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '8px',
  };

  const ratingItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 14px',
    backgroundColor: '#0f0f1e',
    borderRadius: '6px',
    border: '1px solid rgba(212, 175, 55, 0.1)',
  };

  const ratingNameStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#9ca3af',
    textTransform: 'capitalize' as const,
  };

  const ratingValueStyle: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 700,
    color: '#d4af37',
  };

  const listStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  };

  const listItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 14px',
    backgroundColor: '#0f0f1e',
    borderRadius: '6px',
    border: '1px solid rgba(212, 175, 55, 0.1)',
  };

  const bulletStyle: React.CSSProperties = {
    width: '6px',
    height: '6px',
    backgroundColor: '#d4af37',
    borderRadius: '50%',
    flexShrink: 0,
  };

  const listTextStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#e5e7eb',
  };

  return (
    <div>
      {/* Basic Info */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Character Info</h3>
        <div style={infoGridStyle}>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>Playbook</span>
            <span style={infoValueStyle}>{npc.playbook}</span>
          </div>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>Vice</span>
            <span style={infoValueStyle}>{npc.vice}</span>
          </div>
          {npc.vicePurveyor && (
            <div style={infoItemStyle}>
              <span style={infoLabelStyle}>Purveyor</span>
              <span style={infoValueStyle}>{npc.vicePurveyor}</span>
            </div>
          )}
        </div>
      </div>

      {/* Action Ratings */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Action Ratings</h3>
        <div style={ratingsGridStyle}>
          {Object.entries(npc.actionRatings).map(
            ([action, rating]) =>
              rating > 0 && (
                <div key={action} style={ratingItemStyle}>
                  <span style={ratingNameStyle}>{action}</span>
                  <span style={ratingValueStyle}>{rating}</span>
                </div>
              )
          )}
        </div>
      </div>

      {/* Special Abilities */}
      <div>
        <h3 style={sectionTitleStyle}>Special Abilities</h3>
        <div style={listStyle}>
          {npc.specialAbilities.map((ability, index) => (
            <div key={index} style={listItemStyle}>
              <div style={bulletStyle} />
              <span style={listTextStyle}>{ability}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Mission Detail View
const MissionDetailView = ({ mission }: { mission: GeneratedMission }) => {
  const sectionStyle: React.CSSProperties = {
    marginBottom: '24px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#d4af37',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    marginBottom: '12px',
  };

  const infoGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  };

  const infoItemStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  };

  const infoLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  };

  const infoValueStyle: React.CSSProperties = {
    fontSize: '16px',
    color: '#e5e7eb',
    fontWeight: 500,
  };

  const tagContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
  };

  const tagStyle: React.CSSProperties = {
    padding: '6px 12px',
    backgroundColor: '#0f0f1e',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    borderRadius: '20px',
    fontSize: '13px',
    color: '#e5e7eb',
  };

  const listStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  };

  const listItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '10px 14px',
    backgroundColor: '#0f0f1e',
    borderRadius: '6px',
    border: '1px solid rgba(212, 175, 55, 0.1)',
  };

  const warningIconStyle: React.CSSProperties = {
    color: '#d4af37',
    fontSize: '16px',
    flexShrink: 0,
    marginTop: '2px',
  };

  const listTextStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#e5e7eb',
    lineHeight: 1.5,
  };

  return (
    <div>
      {/* Basic Info */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Mission Details</h3>
        <div style={infoGridStyle}>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>Score Type</span>
            <span style={infoValueStyle}>{mission.scoreType}</span>
          </div>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>Heat Level</span>
            <span style={infoValueStyle}>{mission.heatLevel}</span>
          </div>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>Location</span>
            <span style={infoValueStyle}>{mission.location}</span>
          </div>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>Coin Reward</span>
            <span style={infoValueStyle}>{mission.rewards.coin}</span>
          </div>
        </div>
      </div>

      {/* Faction Involvement */}
      {mission.factionInvolvement.length > 0 && (
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Faction Involvement</h3>
          <div style={tagContainerStyle}>
            {mission.factionInvolvement.map((faction, index) => (
              <span key={index} style={tagStyle}>
                {faction}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Complications */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Complications</h3>
        <div style={listStyle}>
          {mission.complications.map((complication, index) => (
            <div key={index} style={listItemStyle}>
              <span style={warningIconStyle}>⚠</span>
              <span style={listTextStyle}>{complication}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Rewards */}
      {mission.rewards.other.length > 0 && (
        <div>
          <h3 style={sectionTitleStyle}>Additional Rewards</h3>
          <div style={tagContainerStyle}>
            {mission.rewards.other.map((reward, index) => (
              <span key={index} style={tagStyle}>
                {reward}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Location Detail View
const LocationDetailView = ({ location }: { location: GeneratedLocation }) => {
  const sectionStyle: React.CSSProperties = {
    marginBottom: '24px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#d4af37',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    marginBottom: '12px',
  };

  const infoGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  };

  const infoItemStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  };

  const infoLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  };

  const infoValueStyle: React.CSSProperties = {
    fontSize: '16px',
    color: '#e5e7eb',
    fontWeight: 500,
  };

  const tagStyle: React.CSSProperties = {
    display: 'inline-block',
    padding: '6px 14px',
    backgroundColor: '#0f0f1e',
    border: '1px solid rgba(212, 175, 55, 0.3)',
    borderRadius: '20px',
    fontSize: '14px',
    color: '#d4af37',
  };

  const descriptionStyle: React.CSSProperties = {
    fontSize: '15px',
    color: '#9ca3af',
    lineHeight: 1.7,
    fontStyle: 'italic',
    padding: '16px',
    backgroundColor: '#0f0f1e',
    borderRadius: '8px',
    border: '1px solid rgba(212, 175, 55, 0.1)',
  };

  return (
    <div>
      {/* Basic Info */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Location Details</h3>
        <div style={infoGridStyle}>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>District</span>
            <span style={infoValueStyle}>{location.district}</span>
          </div>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>Type</span>
            <span style={infoValueStyle}>{location.buildingType}</span>
          </div>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>Security Level</span>
            <span style={infoValueStyle}>{location.securityLevel}/5</span>
          </div>
          <div style={infoItemStyle}>
            <span style={infoLabelStyle}>Haunted</span>
            <span style={infoValueStyle}>{location.haunted ? 'Yes 👻' : 'No'}</span>
          </div>
        </div>
      </div>

      {/* Faction Turf */}
      <div style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Faction Turf</h3>
        <span style={tagStyle}>{location.factionTurf}</span>
      </div>

      {/* Description */}
      <div>
        <h3 style={sectionTitleStyle}>Description</h3>
        <p style={descriptionStyle}>{location.description}</p>
      </div>
    </div>
  );
};

// Custom Export Dropdown Component
const ExportDropdown = ({
  onExport,
  disabled = false,
  label = 'Export...',
  variant = 'default',
}: {
  onExport: (format: 'pdf' | 'markdown' | 'json') => void;
  disabled?: boolean;
  label?: string;
  variant?: 'default' | 'compact';
}): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExport = (format: 'pdf' | 'markdown' | 'json') => {
    onExport(format);
    setIsOpen(false);
  };

  const isCompact = variant === 'compact';

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: isCompact ? '8px 12px' : '10px 16px',
          backgroundColor: disabled ? '#0f0f1e' : '#16162a',
          color: disabled ? '#6b7280' : '#d4af37',
          border: `1px solid ${disabled ? 'rgba(212, 175, 55, 0.1)' : 'rgba(212, 175, 55, 0.2)'}`,
          borderRadius: '6px',
          fontSize: isCompact ? '13px' : '14px',
          fontWeight: 500,
          cursor: disabled ? 'not-allowed' : 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        {label}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            minWidth: '180px',
            backgroundColor: '#16162a',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          <button
            onClick={() => handleExport('pdf')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
              padding: '12px 16px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#e5e7eb',
              fontSize: '14px',
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.1)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span style={{ fontSize: '16px' }}>📄</span>
            <span>Export as PDF</span>
          </button>
          <button
            onClick={() => handleExport('markdown')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
              padding: '12px 16px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#e5e7eb',
              fontSize: '14px',
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              borderTop: '1px solid rgba(212, 175, 55, 0.1)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.1)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span style={{ fontSize: '16px' }}>📝</span>
            <span>Export as Markdown</span>
          </button>
          <button
            onClick={() => handleExport('json')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
              padding: '12px 16px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#e5e7eb',
              fontSize: '14px',
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              borderTop: '1px solid rgba(212, 175, 55, 0.1)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.1)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span style={{ fontSize: '16px' }}>💾</span>
            <span>Export as JSON</span>
          </button>
        </div>
      )}
    </div>
  );
};

export const ContentLibraryPage = (): JSX.Element => {
  const [content, setContent] = useState<SavedContent[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<SavedContent | null>(null);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setIsLoading(true);
      const data = await getAllContent();
      setContent(data);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.trim()) {
      const results = await searchContent(searchQuery);
      setContent(results);
    } else {
      loadContent();
    }
  };

  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this item?')) {
      await deleteContent(id);
      loadContent();
      if (selectedItem?.id === id) {
        setSelectedItem(null);
      }
    }
  };

  const handleExportItem = async (item: SavedContent, format: 'pdf' | 'markdown' | 'json') => {
    try {
      switch (format) {
        case 'pdf':
          await exportAsPdf(item.content, item.type);
          break;
        case 'markdown':
          exportAsMarkdown(item.content, item.type);
          break;
        case 'json':
          exportAsJson(item.content, item.type);
          break;
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const handleExportAll = async (format: 'pdf' | 'markdown' | 'json') => {
    try {
      if (filteredContent.length === 0) {
        alert('No content to export');
        return;
      }

      switch (format) {
        case 'pdf':
          await exportMultipleAsPdf(filteredContent);
          break;
        case 'markdown':
          exportMultipleAsMarkdown(filteredContent);
          break;
        case 'json':
          exportMultipleAsJson(filteredContent);
          break;
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const filteredContent =
    selectedType === 'all' ? content : content.filter(item => item.type === selectedType);

  const typeLabels: Record<string, string> = {
    all: 'All',
    npc: 'NPCs',
    mission: 'Missions',
    location: 'Locations',
  };

  const renderDetailContent = (item: SavedContent) => {
    switch (item.type) {
      case 'npc':
        return <NPCDetailView npc={item.content as GeneratedNpc} />;
      case 'mission':
        return <MissionDetailView mission={item.content as GeneratedMission} />;
      case 'location':
        return <LocationDetailView location={item.content as GeneratedLocation} />;
      default:
        return null;
    }
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    maxWidth: '900px',
    margin: '0 auto',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '32px',
  };

  const iconContainerStyle: React.CSSProperties = {
    padding: '12px',
    backgroundColor: '#16162a',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    borderRadius: '8px',
    color: '#d4af37',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#d4af37',
    margin: '0 0 4px 0',
    fontFamily: 'Cinzel, serif',
  };

  const subtitleStyle: React.CSSProperties = {
    color: '#9ca3af',
    margin: 0,
  };

  const searchCardStyle: React.CSSProperties = {
    backgroundColor: '#16162a',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    marginBottom: '32px',
  };

  const searchRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
  };

  const inputContainerStyle: React.CSSProperties = {
    flex: 1,
    position: 'relative',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px 12px 44px',
    backgroundColor: '#0f0f1e',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    borderRadius: '8px',
    color: '#e5e7eb',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s ease',
  };

  const searchIconStyle: React.CSSProperties = {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#6b7280',
    pointerEvents: 'none',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '12px 24px',
    backgroundColor: '#d4af37',
    color: '#0f0f1e',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
  };

  const filterContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  };

  const getFilterButtonStyle = (isSelected: boolean): React.CSSProperties => ({
    padding: '8px 16px',
    borderRadius: '6px',
    border: isSelected ? 'none' : '1px solid rgba(212, 175, 55, 0.2)',
    backgroundColor: isSelected ? '#d4af37' : '#0f0f1e',
    color: isSelected ? '#0f0f1e' : '#9ca3af',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textTransform: 'capitalize' as const,
  });

  const loadingContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px 0',
    gap: '16px',
  };

  const spinnerStyle: React.CSSProperties = {
    width: '40px',
    height: '40px',
    border: '2px solid rgba(212, 175, 55, 0.2)',
    borderTopColor: '#d4af37',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  };

  const emptyStateStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '64px 24px',
    backgroundColor: '#16162a',
    borderRadius: '12px',
    border: '1px solid rgba(212, 175, 55, 0.1)',
  };

  const emptyTitleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    color: '#d4af37',
    margin: '0 0 8px 0',
    fontFamily: 'Cinzel, serif',
  };

  const emptyTextStyle: React.CSSProperties = {
    color: '#6b7280',
    maxWidth: '400px',
    margin: '0 auto',
  };

  const itemCountStyle: React.CSSProperties = {
    color: '#6b7280',
    fontSize: '14px',
    marginBottom: '16px',
  };

  const contentListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: '#16162a',
    borderRadius: '12px',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    overflow: 'hidden',
    transition: 'border-color 0.2s ease',
    cursor: 'pointer',
  };

  const cardHeaderStyle: React.CSSProperties = {
    padding: '20px 24px',
    borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const cardTitleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#d4af37',
    margin: 0,
    fontFamily: 'Cinzel, serif',
  };

  const cardContentStyle: React.CSSProperties = {
    padding: '20px 24px',
  };

  const cardMetaStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  };

  const typeBadgeStyle: React.CSSProperties = {
    padding: '4px 12px',
    backgroundColor: 'rgba(212, 175, 55, 0.1)',
    color: '#d4af37',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    border: '1px solid rgba(212, 175, 55, 0.2)',
  };

  const dateStyle: React.CSSProperties = {
    color: '#6b7280',
    fontSize: '13px',
  };

  const tagsContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '6px',
  };

  const tagStyle: React.CSSProperties = {
    padding: '4px 10px',
    backgroundColor: '#0f0f1e',
    border: '1px solid rgba(212, 175, 55, 0.1)',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#9ca3af',
  };

  const actionsStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-end',
  };

  const viewButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    backgroundColor: 'rgba(212, 175, 55, 0.1)',
    color: '#d4af37',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  const deleteButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    backgroundColor: 'rgba(220, 38, 38, 0.1)',
    color: '#ef4444',
    border: '1px solid rgba(220, 38, 38, 0.2)',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  // Modal styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(4px)',
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: '#16162a',
    borderRadius: '16px',
    border: '1px solid rgba(212, 175, 55, 0.3)',
    maxWidth: '700px',
    width: '100%',
    maxHeight: '85vh',
    overflow: 'auto',
    position: 'relative',
  };

  const modalHeaderStyle: React.CSSProperties = {
    padding: '24px',
    borderBottom: '1px solid rgba(212, 175, 55, 0.2)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    backgroundColor: '#16162a',
    zIndex: 10,
  };

  const modalTitleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#d4af37',
    margin: 0,
    fontFamily: 'Cinzel, serif',
  };

  const closeButtonStyle: React.CSSProperties = {
    padding: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const modalContentStyle: React.CSSProperties = {
    padding: '24px',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={iconContainerStyle}>
          <LibraryIcon />
        </div>
        <div>
          <h1 style={titleStyle}>Content Library</h1>
          <p style={subtitleStyle}>Manage your generated NPCs, missions, and locations</p>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div style={searchCardStyle}>
        <div style={searchRowStyle}>
          <div style={inputContainerStyle}>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Search saved content..."
              style={inputStyle}
              onFocus={e => {
                e.currentTarget.style.borderColor = '#d4af37';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.1)';
              }}
              onBlur={e => {
                e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
            <div style={searchIconStyle}>
              <SearchIcon />
            </div>
          </div>
          <button
            onClick={handleSearch}
            style={buttonStyle}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#e5c158';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#d4af37';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Search
          </button>
        </div>

        {/* Filter Tabs */}
        <div style={filterContainerStyle}>
          {['all', 'npc', 'mission', 'location'].map(type => {
            const isSelected = selectedType === type;
            return (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                style={getFilterButtonStyle(isSelected)}
                onMouseEnter={e => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.4)';
                    e.currentTarget.style.color = '#d4af37';
                  }
                }}
                onMouseLeave={e => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
                    e.currentTarget.style.color = '#9ca3af';
                  }
                }}
              >
                {typeLabels[type]}
              </button>
            );
          })}
        </div>

        {/* Batch Export */}
        <div
          style={{
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid rgba(212, 175, 55, 0.1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ color: '#9ca3af', fontSize: '14px' }}>
              Export {filteredContent.length} filtered items:
            </span>
            <ExportDropdown
              onExport={handleExportAll}
              disabled={filteredContent.length === 0}
              label="Select format..."
            />
          </div>
        </div>
      </div>

      {/* Content List */}
      {isLoading ? (
        <div style={loadingContainerStyle}>
          <div style={spinnerStyle} />
          <p style={{ color: '#9ca3af', margin: 0 }}>Loading your content...</p>
        </div>
      ) : filteredContent.length === 0 ? (
        <div style={emptyStateStyle}>
          <EmptyIcon />
          <h3 style={emptyTitleStyle}>No Content Found</h3>
          <p style={emptyTextStyle}>
            {searchQuery
              ? 'No results match your search. Try different keywords or clear the filter.'
              : 'Your library is empty. Generate some NPCs, missions, or locations and save them to see them here!'}
          </p>
        </div>
      ) : (
        <div>
          <p style={itemCountStyle}>
            Showing {filteredContent.length} item{filteredContent.length !== 1 ? 's' : ''}
          </p>

          <div style={contentListStyle}>
            {filteredContent.map(item => (
              <div
                key={item.id}
                style={cardStyle}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.3)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
                }}
              >
                <div style={cardHeaderStyle}>
                  <h3 style={cardTitleStyle}>{item.name}</h3>
                  <div style={actionsStyle}>
                    <button
                      onClick={() => setSelectedItem(item)}
                      style={viewButtonStyle}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.2)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.1)';
                      }}
                    >
                      <EyeIcon />
                      View
                    </button>
                    <ExportDropdown
                      onExport={format => handleExportItem(item, format)}
                      label="Export..."
                      variant="compact"
                    />
                    <button
                      onClick={() => handleDelete(item.id)}
                      style={deleteButtonStyle}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.2)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.1)';
                      }}
                    >
                      <TrashIcon />
                      Delete
                    </button>
                  </div>
                </div>
                <div style={cardContentStyle}>
                  <div style={cardMetaStyle}>
                    <span style={typeBadgeStyle}>{item.type}</span>
                    <span style={dateStyle}>
                      {new Date(item.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </div>

                  {item.tags.length > 0 && (
                    <div style={tagsContainerStyle}>
                      {item.tags.map((tag, index) => (
                        <span key={index} style={tagStyle}>
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedItem && (
        <div style={overlayStyle} onClick={() => setSelectedItem(null)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <h2 style={modalTitleStyle}>{selectedItem.name}</h2>
              <button
                onClick={() => setSelectedItem(null)}
                style={closeButtonStyle}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.color = '#d4af37';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                <CloseIcon />
              </button>
            </div>
            <div style={modalContentStyle}>{renderDetailContent(selectedItem)}</div>
            {/* Export dropdown in modal */}
            <div
              style={{
                padding: '24px',
                borderTop: '1px solid rgba(212, 175, 55, 0.2)',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  color: '#9ca3af',
                  fontSize: '14px',
                  marginRight: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Export this item:
              </span>
              {selectedItem && (
                <ExportDropdown
                  onExport={format => handleExportItem(selectedItem, format)}
                  label="Select format..."
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Export utility functions (moved here for Module Federation compatibility)
type ExportFormat = 'pdf' | 'markdown' | 'json';
type ContentType = 'npc' | 'mission' | 'location';

interface ExportOptions {
  filename?: string;
  includeTimestamp?: boolean;
}

function generateFilename(baseName: string, format: ExportFormat, includeTimestamp = true): string {
  const timestamp = includeTimestamp ? `-${new Date().toISOString().split('T')[0]}` : '';
  const extension = format === 'pdf' ? '.pdf' : format === 'markdown' ? '.md' : '.json';
  return `${baseName}${timestamp}${extension}`;
}

function downloadFile(content: string | Blob, filename: string, mimeType: string): void {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function generateNpcMarkdown(npc: GeneratedNpc): string {
  return `# ${npc.name}

**Playbook:** ${npc.playbook}
${npc.vice ? `**Vice:** ${npc.vice}${npc.vicePurveyor ? ` (${npc.vicePurveyor})` : ''}` : ''}
${npc.background ? `**Background:** ${npc.background}` : ''}
${npc.heritage ? `**Heritage:** ${npc.heritage}` : ''}
${npc.factionAffiliation ? `**Faction:** ${npc.factionAffiliation}` : ''}

## Action Ratings

${Object.entries(npc.actionRatings)
  .filter(([, rating]) => rating > 0)
  .map(([action, rating]) => `- **${action.charAt(0).toUpperCase() + action.slice(1)}:** ${rating}`)
  .join('\n')}

## Special Abilities

${npc.specialAbilities.map(ability => `- ${ability}`).join('\n')}
${
  npc.contacts && npc.contacts.length > 0
    ? `
## Contacts

${npc.contacts.map(contact => `- ${contact}`).join('\n')}
`
    : ''
}

---
*Generated by Blades in the Dark Content Generator*
`;
}

function generateMissionMarkdown(mission: GeneratedMission): string {
  return `# Score: ${mission.target}

**Type:** ${mission.scoreType}
**Heat Level:** ${mission.heatLevel}/6
**Location:** ${mission.location}

## Target

${mission.target}

## Rewards

- **Coin:** ${mission.rewards.coin}
${mission.rewards.other.length > 0 ? `- **Other:** ${mission.rewards.other.join(', ')}` : ''}

## Complications

${mission.complications.map(comp => `- ${comp}`).join('\n')}

${
  mission.factionInvolvement.length > 0
    ? `## Faction Involvement

${mission.factionInvolvement.map(faction => `- ${faction}`).join('\n')}
`
    : ''
}

---
*Generated by Blades in the Dark Content Generator*
`;
}

function generateLocationMarkdown(location: GeneratedLocation): string {
  return `# ${location.name}

**District:** ${location.district}
**Type:** ${location.buildingType}
**Security Level:** ${location.securityLevel}/4
**Haunted:** ${location.haunted ? 'Yes' : 'No'}

## Description

${location.description}

${
  location.factionTurf
    ? `## Faction Turf

Controlled by: ${location.factionTurf}
`
    : ''
}

---
*Generated by Blades in the Dark Content Generator*
`;
}

function generateMarkdown(
  content: GeneratedNpc | GeneratedMission | GeneratedLocation,
  type: ContentType
): string {
  switch (type) {
    case 'npc':
      return generateNpcMarkdown(content as GeneratedNpc);
    case 'mission':
      return generateMissionMarkdown(content as GeneratedMission);
    case 'location':
      return generateLocationMarkdown(content as GeneratedLocation);
    default:
      return '';
  }
}

function exportAsMarkdown(
  content: GeneratedNpc | GeneratedMission | GeneratedLocation,
  type: ContentType,
  options: ExportOptions = {}
): void {
  const markdown = generateMarkdown(content, type);
  const baseName =
    type === 'npc'
      ? (content as GeneratedNpc).name
      : type === 'mission'
        ? (content as GeneratedMission).target
        : (content as GeneratedLocation).name;
  const filename =
    options.filename ||
    generateFilename(
      baseName.toLowerCase().replace(/\s+/g, '-'),
      'markdown',
      options.includeTimestamp
    );
  downloadFile(markdown, filename, 'text/markdown');
}

function exportAsJson(
  content: GeneratedNpc | GeneratedMission | GeneratedLocation,
  type: ContentType,
  options: ExportOptions = {}
): void {
  const exportData = { type, content, exportedAt: new Date().toISOString() };
  const baseName =
    type === 'npc'
      ? (content as GeneratedNpc).name
      : type === 'mission'
        ? (content as GeneratedMission).target
        : (content as GeneratedLocation).name;
  const filename =
    options.filename ||
    generateFilename(baseName.toLowerCase().replace(/\s+/g, '-'), 'json', options.includeTimestamp);
  downloadFile(JSON.stringify(exportData, null, 2), filename, 'application/json');
}

async function exportAsPdf(
  content: GeneratedNpc | GeneratedMission | GeneratedLocation,
  type: ContentType,
  options: ExportOptions = {}
): Promise<void> {
  const markdown = generateMarkdown(content, type);
  const container = document.createElement('div');
  container.style.cssText =
    'padding: 40px; max-width: 800px; margin: 0 auto; font-family: Georgia, serif; line-height: 1.6; color: #1a1a2e; background: white;';

  const htmlContent = markdown
    .replace(
      /^# (.*$)/gm,
      '<h1 style="color: #8B0000; border-bottom: 2px solid #d4af37; padding-bottom: 10px; font-family: Cinzel, serif;">$1</h1>'
    )
    .replace(
      /^## (.*$)/gm,
      '<h2 style="color: #8B0000; margin-top: 30px; font-family: Cinzel, serif;">$1</h2>'
    )
    .replace(/^### (.*$)/gm, '<h3 style="color: #333;">$1</h3>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/^\* (.*$)/gm, '<li style="margin-left: 20px;">$1</li>')
    .replace(/<li>(.*?)<\/li>/g, '<li style="margin-left: 20px; margin-bottom: 5px;">$1</li>')
    .replace(/---/g, '<hr style="border: none; border-top: 1px solid #d4af37; margin: 30px 0;">')
    .replace(/\n\n/g, '</p><p style="margin-bottom: 15px;">')
    .replace(/^(?!<[hlu])/, '<p style="margin-bottom: 15px;">')
    .replace(/$/gm, '</p>');

  container.innerHTML = `<div style="text-align: center; margin-bottom: 30px;"><div style="font-size: 12px; color: #666; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 10px;">Blades in the Dark</div></div>${htmlContent}`;
  document.body.appendChild(container);

  try {
    const html2pdf = (await import('html2pdf.js')).default;
    const baseName =
      type === 'npc'
        ? (content as GeneratedNpc).name
        : type === 'mission'
          ? (content as GeneratedMission).target
          : (content as GeneratedLocation).name;
    const filename =
      options.filename ||
      generateFilename(
        baseName.toLowerCase().replace(/\s+/g, '-'),
        'pdf',
        options.includeTimestamp
      );

    const opt = {
      margin: [20, 20, 20, 20] as [number, number, number, number],
      filename: filename,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
    };

    await html2pdf().set(opt).from(container).save();
  } finally {
    document.body.removeChild(container);
  }
}

function exportMultipleAsMarkdown(items: SavedContent[], options: ExportOptions = {}): void {
  const content = items
    .map(item => {
      const header = `# ${item.name}\n\n**Type:** ${item.type.toUpperCase()}\n`;
      const body = generateMarkdown(item.content, item.type);
      return `${header}\n${body}\n---\n\n`;
    })
    .join('\n');
  const filename =
    options.filename ||
    generateFilename('bid-content-library', 'markdown', options.includeTimestamp);
  downloadFile(content, filename, 'text/markdown');
}

function exportMultipleAsJson(items: SavedContent[], options: ExportOptions = {}): void {
  const exportData = {
    exportType: 'batch',
    exportedAt: new Date().toISOString(),
    itemCount: items.length,
    items: items.map(item => ({
      id: item.id,
      type: item.type,
      name: item.name,
      content: item.content,
      tags: item.tags,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    })),
  };
  const filename =
    options.filename || generateFilename('bid-content-library', 'json', options.includeTimestamp);
  downloadFile(JSON.stringify(exportData, null, 2), filename, 'application/json');
}

async function exportMultipleAsPdf(
  items: SavedContent[],
  options: ExportOptions = {}
): Promise<void> {
  const container = document.createElement('div');
  container.style.cssText =
    'padding: 40px; max-width: 800px; margin: 0 auto; font-family: Georgia, serif; line-height: 1.6; color: #1a1a2e; background: white;';

  let htmlContent = `<div style="text-align: center; margin-bottom: 30px;"><h1 style="color: #8B0000; font-family: Cinzel, serif; border-bottom: 3px solid #d4af37; padding-bottom: 15px;">Content Library Export</h1><p style="color: #666;">${items.length} items exported</p><p style="color: #999; font-size: 12px;">${new Date().toLocaleDateString()}</p></div>`;

  items.forEach(item => {
    const markdown = generateMarkdown(item.content, item.type);
    const itemHtml = markdown
      .replace(
        /^# (.*$)/gm,
        '<h1 style="color: #8B0000; border-bottom: 2px solid #d4af37; padding-bottom: 10px; font-family: Cinzel, serif; page-break-before: always;">$1</h1>'
      )
      .replace(
        /^## (.*$)/gm,
        '<h2 style="color: #8B0000; margin-top: 30px; font-family: Cinzel, serif;">$1</h2>'
      )
      .replace(/^### (.*$)/gm, '<h3 style="color: #333;">$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/^\* (.*$)/gm, '<li style="margin-left: 20px;">$1</li>')
      .replace(/<li>(.*?)<\/li>/g, '<li style="margin-left: 20px; margin-bottom: 5px;">$1</li>')
      .replace(/---/g, '<hr style="border: none; border-top: 1px solid #d4af37; margin: 30px 0;">')
      .replace(/\n\n/g, '</p><p style="margin-bottom: 15px;">')
      .replace(/^(?!<[hlu])/, '<p style="margin-bottom: 15px;">')
      .replace(/$/gm, '</p>');
    htmlContent += `<div style="margin-bottom: 40px;"><div style="background: #f5f5f5; padding: 10px 15px; border-left: 4px solid #d4af37; margin-bottom: 20px;"><span style="font-weight: bold; text-transform: uppercase; font-size: 12px; color: #666;">${item.type}</span>${item.tags.length > 0 ? `<span style="margin-left: 15px; color: #999;">Tags: ${item.tags.join(', ')}</span>` : ''}</div>${itemHtml}</div>`;
  });

  container.innerHTML = htmlContent;
  document.body.appendChild(container);

  try {
    const html2pdf = (await import('html2pdf.js')).default;
    const filename =
      options.filename || generateFilename('bid-content-library', 'pdf', options.includeTimestamp);

    const opt = {
      margin: [20, 20, 20, 20] as [number, number, number, number],
      filename: filename,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
    };

    await html2pdf().set(opt).from(container).save();
  } finally {
    document.body.removeChild(container);
  }
}
